import React, { useState, useEffect, useRef } from 'react';
import useWebSocket from '../wsHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { useParams, useLocation } from 'react-router-dom';
import { sensorInfoAtom, SensorInfo, ParsedSensorInfo, Weather, RainGauge, RiverHeight, Tank, Kiln, SoilMt, JInfo, Esensor, ExtendedDeviceEUIProps, eSensorChartAtom, EsensorChart } from '../atoms-types/eSensors';
import { WidthProvider, Responsive } from 'react-grid-layout'
import { Divider, Card, Typography, CardContent, Box, useTheme, useMediaQuery, Skeleton } from '@mui/material';
import EsensorChartHandler from '../handlers/eSensorGraph-handler';
import { Layout } from '../atoms-types/layout';
import { convertWindDirection } from '../converters/windDirectionConverter';
import { endpointLink, websocketLink } from '../../index';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Skeleton card component for loading state
const SkeletonCard = () => (
  <Card sx={{ height: '100%', width: '100%' }}>
    <CardContent>
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="rectangular" height={3} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <Skeleton variant="text" width={100} />
          <Skeleton variant="text" width={20} />
          <Skeleton variant="text" width={100} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const EnvironGrid: React.FC = () => {
  const { Group } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [sensorInfo, setSensorInfo] = useAtom(sensorInfoAtom);
  const [selectedSensor, setSelectedSensor] = useState<ExtendedDeviceEUIProps | null>(null);
  const { messages } = useWebSocket(websocketLink, ['sensor']);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const setEsensorChart = useSetAtom(eSensorChartAtom);
  const [selectedRange, setSelectedRange] = useState<string>("Last 24 hours");
  
  // Track the last processed message index to avoid reprocessing
  const lastProcessedIndexRef = useRef(-1);

  // Clear query cache when navigating away from environment pages
  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['sensorMetadata'] });
      setSensorInfo({});
      setEsensorChart([]);
    };
  }, [queryClient, setSensorInfo, setEsensorChart]);

  // Effect for updating sensor info
  useEffect(() => {
    if (messages.length === 0) return;
    
    // Only process messages we haven't seen before
    const newMessages = messages.slice(lastProcessedIndexRef.current + 1);
    lastProcessedIndexRef.current = messages.length - 1;
    
    newMessages.forEach((messageObj) => {
      const { channel, message } = messageObj;
      try {
        if (channel === 'sensor' && message) {
          const { deviceEUI, ...jInfo } = message as Esensor;
          
          // Only update if we have sensor info for this deviceEUI
          setSensorInfo((prev) => {
            if (!prev[deviceEUI]) {
              return prev;
            }
            
            const updatedSensors = prev[deviceEUI].map(sensor => ({
              ...sensor,
              jInfo
            }));

            return {
              ...prev,
              [deviceEUI]: updatedSensors
            };
          });
        }
      } catch (error) {
        console.error('Failed to process websocket message:', error);
      }
    });
  }, [messages, setSensorInfo]);

  // Reset message index when dependencies change
  useEffect(() => {
    lastProcessedIndexRef.current = -1;
  }, [selectedSensor]);

  // Query for sensor data
  const { data, isLoading } = useQuery({
    queryKey: ['sensorMetadata', Group],
    queryFn: async () => {
      const response = await axios.get<SensorInfo[]>(`${endpointLink}sensor/${Group}`);
      const data = response.data;
      
      // Update the sensorInfo atom with data
      const newSensorInfo: { [deviceEUI: string]: SensorInfo[] } = {};
      data.forEach(sensor => {
        if (!newSensorInfo[sensor.deviceEUI]) {
          newSensorInfo[sensor.deviceEUI] = [];
        }
        newSensorInfo[sensor.deviceEUI].push(sensor);
      });
      updateSensorInfo(newSensorInfo);
      
      return data;
    }
  });

  // Prefetch data for other environment groups
  useEffect(() => {
    const prefetchGroups = ['weather', 'water', 'soil'].filter(group => group !== Group);
    prefetchGroups.forEach(group => {
      queryClient.prefetchQuery({
        queryKey: ['sensorMetadata', group],
        queryFn: async () => {
          const response = await axios.get<SensorInfo[]>(`${endpointLink}sensor/${group}`);
          return response.data;
        }
      });
    });
  }, [Group, queryClient]);

  // Add effect to set first sensor as selected when data loads
  useEffect(() => {
    if (data && data.length > 0) {
      const firstSensor = data[0];
      const displays = Array.isArray(firstSensor.Display) ? firstSensor.Display : [];
      if (displays.length > 0) {
        setSelectedSensor({
          deviceEUI: firstSensor.deviceEUI,
          Display: displays,
          subClass: firstSensor.Type === "Weather" ? "temp" : null
        });
      }
    }
  }, [data]);

  const updateSensorInfo = (newSensorInfo: { [deviceEUI: string]: SensorInfo[] }) => { 
    const updatedSensorInfo: { [deviceEUI: string]: ParsedSensorInfo[] } = {};
    
    Object.entries(newSensorInfo).forEach(([deviceEUI, sensors]) => {
      updatedSensorInfo[deviceEUI] = sensors.map(sensor => {
        try {
          const parsedSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: typeof sensor.jInfo === 'string' ? JSON.parse(sensor.jInfo) : sensor.jInfo
          };
          return parsedSensor as ParsedSensorInfo;
        } catch (error) {
          console.error(`Error parsing sensor data for deviceEUI ${deviceEUI}:`, error);
          // Return a default JInfo object based on the sensor type
          const defaultSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: {} as JInfo // Default empty JInfo object
          };
          return defaultSensor as ParsedSensorInfo;
        }
      });
    });
    
    console.log("Final updatedSensorInfo:", updatedSensorInfo);
    setSensorInfo(updatedSensorInfo);

    // Set the first sensor as selected after updating sensorInfo
    const firstDeviceEUI = Object.keys(updatedSensorInfo)[0];
    if (firstDeviceEUI && updatedSensorInfo[firstDeviceEUI]?.length > 0) {
      const firstSensor = updatedSensorInfo[firstDeviceEUI][0];
      const displays = Array.isArray(firstSensor.Display) ? firstSensor.Display : [];
      if (displays.length > 0) {
        setSelectedSensor({
          deviceEUI: firstDeviceEUI,
          Display: displays,
          subClass: firstSensor.Type === "Weather" ? "temp" : null
        });
      }
    }
  }

  // Type mapping for sensor types
  const sensorTypeMap: Record<string, JInfo> = {
    "Weather": {} as Weather,
    "RainGauge": {} as RainGauge,
    "RiverHeight": {} as RiverHeight,
    "Tank": {} as Tank,
    "Kiln": {} as Kiln,
    "SoilMt": {} as SoilMt
  };

  // Type guard function to get the correct sensor type
  const getSensorValue = (sensorData: JInfo, sensorType: string, display: string): number => {
    const type = sensorTypeMap[sensorType];
    if (!type) return 0;
    
    const value = (sensorData as typeof type)[display as keyof typeof type];
    return typeof value === 'number' ? value : 0;
  };

  // Create layout based on metadata
  const createLayout = (cols: number): Layout[] => {
    if (!sensorInfo) return [];

    const layouts: Layout[] = [];
    let x = 0;
    let y = 0;
    const maxX = cols; // Use the number of columns passed in
    const boxWidth = cols >= 12 ? 3 : cols >= 6 ? 3 : 3; // Set box width to 3 columns for all screen sizes

    // Iterate over the keyed sensorInfo object
    Object.entries(sensorInfo).forEach(([deviceEUI, sensors]) => {
      sensors.forEach(sensor => {
        // For weather sensors, create two cards
        if (sensor.Type === "Weather") {
          // First card for temperature and humidity
          if (x >= maxX) {
            x = 0;
            y++;
          }
          layouts.push({
            i: `${deviceEUI}-temp`,
            x,
            y,
            w: boxWidth,
            h: 2.5,
            minW: 2,
            minH: 2
          });
          x += boxWidth;

          // Second card for wind and pressure
          if (x >= maxX) {
            x = 0;
            y++;
          }
          layouts.push({
            i: `${deviceEUI}-wind`,
            x,
            y,
            w: boxWidth,
            h: 2.5,
            minW: 2,
            minH: 2
          });
          x += boxWidth;
        } else {
          // For all other sensors, create one card
          if (x >= maxX) {
            x = 0;
            y++;
          }
          layouts.push({
            i: `${deviceEUI}`,
            x,
            y,
            w: boxWidth,
            h: 2.5,
            minW: 2,
            minH: 2
          });
          x += boxWidth;
        }
      });
    });

    // Add chart at the bottom
    layouts.push({
      i: 'SensorChart',
      x: 0,
      y: y + 1,
      w: cols, // Use full width of available columns
      h: 6,
      minW: 6,
      minH: 6
    });

    return layouts;
  };

  // Create layouts for different breakpoints
  const layouts = React.useMemo(() => ({
    lg: createLayout(12), // 4 boxes per row
    md: createLayout(6),  // 2 boxes per row
    sm: createLayout(3),  // 1 box per row
    xs: createLayout(1)   // 1 box per row
  }), [sensorInfo]);

  // Create a default layout for the skeleton state
  const skeletonLayout = React.useMemo(() => ({
    lg: Array(6).fill(null).map((_, i) => ({
      i: `skeleton-${i}`,
      x: (i % 3) * 4,
      y: Math.floor(i / 3) * 3,
      w: 4,
      h: 3,
      minW: 2,
      minH: 2
    })),
    md: Array(4).fill(null).map((_, i) => ({
      i: `skeleton-${i}`,
      x: (i % 2) * 3,
      y: Math.floor(i / 2) * 3,
      w: 3,
      h: 3,
      minW: 2,
      minH: 2
    })),
    sm: Array(2).fill(null).map((_, i) => ({
      i: `skeleton-${i}`,
      x: 0,
      y: i * 3,
      w: 3,
      h: 3,
      minW: 2,
      minH: 2
    })),
    xs: [{
      i: 'skeleton-0',
      x: 0,
      y: 0,
      w: 1,
      h: 3,
      minW: 1,
      minH: 2
    }]
  }), []);

  // Get the unit based on the display type
  const getUnit = (displayType: string) => {
    switch (displayType) {
      case 'TempC':
        return '°C';
      case 'TempF':
        return '°F';
      case 'Humidity':
        return '%';
      case 'WindAvg':
      case 'WindMax':
        return 'mph';
      case 'WindDir':
        return '°';
      case 'BaroPress':
        return 'hPa';
      case 'Height':
        return 'in';
      default:
        return '';
    }
  };

  // Get the color based on the display type and sensor type
  const getColor = (displayType: string, sensorType?: string) => {
    // First check sensor type for specific sensors
    if (sensorType === "SoilMt") {
      return '#795548'; // Medium brown color for soil sensors
    }
    if (sensorType === "Kiln") {
      return 'error.main'; // Error color for kiln sensors
    }
    if (sensorType === "Tank") {
      return '#2196f3'; // Blue color for tank sensors
    }

    // Then check display type for other sensors
    switch (displayType) {
      case 'TempC':
      case 'TempF':
        return 'success.main';
      case 'Humidity':
        return 'warning.main';
      case 'WindAvg':
      case 'WindMax':
      case 'WindDir':
      case 'BaroPress':
        return '#2196f3'; // Blue color for wind-related displays
      case 'Height':
        return '#9c27b0'; // Purple color for river height
      case 'Inches':
        return 'error.main';
      default:
        return 'primary.main';
    }
  };

  // Create a card component based on the display type and sensor data
  const createCard = (layoutId: string) => {
    if (!data) return null;

    // Special case for chart
    if (layoutId === 'SensorChart') {
      return (
        <div key="SensorChart" style={{ minHeight: "500px" }} className='card-gauge'>
          <Card style={{ minHeight: "500px", width: "100%" }}>
            <EsensorChartHandler 
              deviceEUI={selectedSensor?.deviceEUI || ""} 
              Display={selectedSensor?.Display || []}
              subClass={selectedSensor?.subClass || null}
              sensorInfo={selectedSensor?.deviceEUI ? sensorInfo[selectedSensor.deviceEUI]?.[0] : undefined}
              onRangeChange={setSelectedRange}
            />
          </Card>
        </div>
      );
    }

    // Handle split weather cards
    if (layoutId.endsWith('-temp') || layoutId.endsWith('-wind')) {
      const deviceEUI = layoutId.split('-')[0];
      const cardType = layoutId.split('-')[1] as 'temp' | 'wind';
      
      // Find the sensor metadata from the keyed structure
      const sensors = sensorInfo[deviceEUI];
      if (!sensors || sensors.length === 0) return null;
      const sensor = sensors[0];

      // Get the value from the sensor's jInfo
      const sensorData = sensor.jInfo;
      if (!sensorData) return null;

      // Get all displays for the sensor
      const allDisplays = Array.isArray(sensor.Display) ? sensor.Display : [];
      
      // Filter displays for this specific card
      const cardDisplays = cardType === 'temp' 
        ? allDisplays.filter(d => ['TempF', 'Humidity'].includes(d))
        : allDisplays.filter(d => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d));

      if (cardDisplays.length === 0) return null;

      return (
        <div 
          key={layoutId} 
          style={{ minHeight: "150px" }} 
          className='card-gauge'
        >
          <Card 
            key={`${layoutId}-${JSON.stringify(sensorData)}`}
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              width: '100%', 
              height: '100%',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 6
              }
            }}
            onClick={() => {
              if (cardDisplays.length > 0) {
                setSelectedSensor({
                  deviceEUI,
                  Display: allDisplays,
                  subClass: cardType
                });
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {cardDisplays.map((displayType: string) => {
                  const value = getSensorValue(sensorData, sensor.Type, displayType).toFixed(2);
                  return (
                    <Box 
                      key={displayType} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        px: 1
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        {displayType}:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        <Typography variant="h4" sx={{ color: 'text.primary' }}>
                          {displayType === 'WindDir' 
                            ? `${value}° ${convertWindDirection(Number(value)).direction}`
                            : value}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.primary', ml: 0.5 }}>
                          {displayType === 'WindDir' ? '' : getUnit(displayType)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ mt: 1 }}>
                <Divider sx={{ borderBottomWidth: '3px', borderColor: cardDisplays.length > 0 ? getColor(cardDisplays[0], sensor.Type) : 'primary.main' }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'text.secondary'
                    }}
                  >
                    {sensor.Class} {cardType === 'temp' ? '(Temp)' : '(Wind)'}
                  </Typography>
                  {sensor.Location && (
                    <>
                      <Typography variant="h6" sx={{ color: 'text.secondary' }}>•</Typography>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: 'text.secondary'
                        }}
                      >
                        {sensor.Location}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Regular card handling (non-weather sensors)
    const deviceEUI = layoutId;
    
    // Find the sensor metadata from the keyed structure
    const sensors = sensorInfo[deviceEUI];
    if (!sensors || sensors.length === 0) return null;
    const sensor = sensors[0];

    // Get the value from the sensor's jInfo
    const sensorData = sensor.jInfo;
    if (!sensorData) return null;

    // Ensure Display is treated as an array of strings
    const displays = Array.isArray(sensor.Display) ? sensor.Display : [];

    return (
      <div 
        key={layoutId} 
        style={{ minHeight: "150px" }} 
        className='card-gauge'
      >
        <Card 
          key={`${layoutId}-${JSON.stringify(sensorData)}`}
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%', 
            height: '100%',
            cursor: 'pointer',
            '&:hover': {
              boxShadow: 6
            }
          }}
          onClick={() => {
            if (displays.length > 0) {
              setSelectedSensor({
                deviceEUI,
                Display: displays,
                subClass: null
              });
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {displays.map((displayType: string) => {
                const value = getSensorValue(sensorData, sensor.Type, displayType).toFixed(2);
                return (
                  <Box 
                    key={displayType} 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      px: 1
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                      {displayType}:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      <Typography variant="h4" sx={{ color: 'text.primary' }}>
                        {displayType === 'WindDir' 
                          ? `${value}° ${convertWindDirection(Number(value)).direction}`
                          : value}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'text.primary', ml: 0.5 }}>
                        {displayType === 'WindDir' ? '' : getUnit(displayType)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ borderBottomWidth: '3px', borderColor: displays.length > 0 ? getColor(displays[0], sensor.Type) : 'primary.main' }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'text.secondary'
                  }}
                >
                  {sensor.Class}
                </Typography>
                {sensor.Location && (
                  <>
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>•</Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.secondary'
                      }}
                    >
                      {sensor.Location}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflowY: isSmallScreen ? 'auto' : 'visible',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      <Box sx={{ minHeight: '100%', width: '100%' }}>
        <ResponsiveGridLayout 
          className="layout" 
          layouts={isLoading ? skeletonLayout : layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
          cols={{ lg: 12, md: 6, sm: 3, xs: 1 }}
          rowHeight={100}
          draggableCancel=".cancelSelectorName"
          isDraggable={false}
          isResizable={false}
        >
          {isLoading 
            ? skeletonLayout.lg.map(l => (
                <div key={l.i} style={{ minHeight: "150px" }}>
                  <SkeletonCard />
                </div>
              ))
            : layouts.lg.map(l => createCard(l.i))
          }
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default EnvironGrid;